import { useState, useEffect } from 'react';
import { AnyPinContent } from '../types';

interface SavedPin {
    id: string;
    content: AnyPinContent;
    timestamp: number;
    isFavorite: boolean;
    thumbnail?: string;
}

const STORAGE_KEY = 'pin-generator-history';
const MAX_HISTORY = 10; // Reduced from 20 to save space

// Strip large base64 images from content before storing
function stripLargeImages(content: AnyPinContent): AnyPinContent {
    const stripped = { ...content };

    // For standard pins, remove the large base64 image
    if ('image' in stripped && typeof stripped.image === 'string') {
        if (stripped.image.length > 1000) {
            (stripped as any).image = '[image-removed-for-storage]';
        }
    }

    // For listicle pins, remove background image
    if ('backgroundImage' in stripped && typeof stripped.backgroundImage === 'string') {
        if (stripped.backgroundImage.length > 1000) {
            (stripped as any).backgroundImage = '[image-removed-for-storage]';
        }
    }

    // For carousel/collage pins, strip user images
    if ('userImages' in stripped && Array.isArray(stripped.userImages)) {
        (stripped as any).userImages = stripped.userImages.map((img: string) =>
            img.length > 1000 ? '[image-removed-for-storage]' : img
        );
    }

    if ('userImageGroups' in stripped && Array.isArray(stripped.userImageGroups)) {
        (stripped as any).userImageGroups = stripped.userImageGroups.map((group: string[]) =>
            group.map(img => img.length > 1000 ? '[image-removed-for-storage]' : img)
        );
    }

    if ('userImage' in stripped && typeof stripped.userImage === 'string') {
        if (stripped.userImage.length > 1000) {
            (stripped as any).userImage = '[image-removed-for-storage]';
        }
    }

    return stripped;
}

export function usePinHistory() {
    const [history, setHistory] = useState<SavedPin[]>([]);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                // Check if storage is too large (> 1MB indicates bloated images)
                if (stored.length > 1000000) {
                    console.warn('Pin history too large, clearing...');
                    localStorage.removeItem(STORAGE_KEY);
                    setHistory([]);
                    return;
                }

                const parsed = JSON.parse(stored);
                // Strip any large images from loaded history
                const cleaned = parsed.map((pin: SavedPin) => ({
                    ...pin,
                    content: stripLargeImages(pin.content)
                }));
                setHistory(cleaned);

                // Re-save cleaned history
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
                } catch {
                    // If still too large, just clear it
                    localStorage.removeItem(STORAGE_KEY);
                    setHistory([]);
                }
            }
        } catch {
            localStorage.removeItem(STORAGE_KEY);
            setHistory([]);
        }
    }, []);

    const saveToStorage = (pins: SavedPin[]) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(pins));
            setHistory(pins);
        } catch (error) {
            // Quota exceeded - remove oldest non-favorite entries and try again
            console.warn('localStorage quota exceeded, cleaning up old pins...');
            const favorites = pins.filter(p => p.isFavorite);
            const nonFavorites = pins.filter(p => !p.isFavorite);

            // Keep only the 3 most recent non-favorites
            const trimmedNonFavorites = nonFavorites.slice(0, 3);
            const trimmedPins = [...favorites, ...trimmedNonFavorites];

            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedPins));
                setHistory(trimmedPins);
            } catch {
                // Still failing - clear all non-favorites
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
                    setHistory(favorites);
                } catch {
                    // Last resort - clear everything
                    localStorage.removeItem(STORAGE_KEY);
                    setHistory([]);
                }
            }
        }
    };

    const savePin = (content: AnyPinContent, thumbnail?: string) => {
        // Strip large images before storing
        const strippedContent = stripLargeImages(content);

        const newPin: SavedPin = {
            id: `pin-${Date.now()}`,
            content: strippedContent,
            timestamp: Date.now(),
            isFavorite: false,
            thumbnail
        };

        const nonFavorites = history.filter(p => !p.isFavorite);
        const favorites = history.filter(p => p.isFavorite);

        const updatedNonFavorites = [newPin, ...nonFavorites].slice(0, MAX_HISTORY);
        const updatedHistory = [...favorites, ...updatedNonFavorites];

        saveToStorage(updatedHistory);
        return newPin.id;
    };

    const toggleFavorite = (id: string) => {
        const updated = history.map(pin =>
            pin.id === id ? { ...pin, isFavorite: !pin.isFavorite } : pin
        );
        saveToStorage(updated);
    };

    const deletePin = (id: string) => {
        const updated = history.filter(pin => pin.id !== id);
        saveToStorage(updated);
    };

    const clearHistory = () => {
        const favoritesOnly = history.filter(p => p.isFavorite);
        saveToStorage(favoritesOnly);
    };

    const getRecent = (limit = 10) =>
        history.filter(p => !p.isFavorite).slice(0, limit);

    const getFavorites = () =>
        history.filter(p => p.isFavorite);

    return {
        history,
        savePin,
        toggleFavorite,
        deletePin,
        clearHistory,
        getRecent,
        getFavorites
    };
}

export type { SavedPin };
